'use client';

import { produce } from 'immer';
import isEqual from 'lodash/isEqual';
import { useSearchParams } from 'next/navigation';
import React, { useEffect, useRef } from 'react';

import DiscoverySection from '@/app/_components/discovery/DiscoverySection';
import {
  comfortOptions,
  driveTypeOptions,
  fuelTypeOptions,
  otherOptions,
  safetyAndTechOptions,
  searchMileageTypeOptions,
  searchPriceTypeOptions,
  searchYearTypeOptions,
  transmissionTypeOptions,
} from '@/constants/filters/filters.constants';
import { minDesktop } from '@/constants/vars.constants';
import { useDealersInfo } from '@/hooks/dealers/dealers.hook';
import { useBodyTypesFilter } from '@/hooks/filters/body-types-filter.hook';
import { useBrandsRangesFilter } from '@/hooks/filters/brands-ranges-filter.hook';
import { useColorsFilter } from '@/hooks/filters/colors-filter.hook';
import { useDoorsFilter } from '@/hooks/filters/doors-filter.hook';
import { useDrivetrainFilter } from '@/hooks/filters/drivetrain-filter.hook';
import { useFeaturesFilter } from '@/hooks/filters/features-filter.hook';
import { useFuelConsumptionFilter } from '@/hooks/filters/fuel-consumption.hook';
import { useFuelTypeFilter } from '@/hooks/filters/fuel-type-filter.hook';
import { useIndexSearch } from '@/hooks/filters/index-search.hook';
import { useMileageFilter } from '@/hooks/filters/mileage-filter.hook';
import { usePriceFilter } from '@/hooks/filters/price-filter.hook';
import { useTransmissionTypeFilter } from '@/hooks/filters/transmission-type-filter.hook';
import { useYearsFilterHook } from '@/hooks/filters/years-filter.hook';
import { useRoutesConfig } from '@/hooks/routes-config';
import { useFiltersQuery } from '@/hooks/used-cars/filters-query.hook';
import { useProvincesDealershipsFilter } from '@/hooks/used-cars/provinces-dealerships-filter.hook';
import { useQueryParams } from '@/hooks/used-cars/query-params.hook';
import { useVehicles } from '@/hooks/used-cars/vehicles.hook';
import { useWindowSize } from '@/hooks/window-size.hook';
import { getImageLink } from '@/services/link.service';
import { parseSearchParams } from '@/services/QueryStringService';
import { getVehiclesWithFilters } from '@/services/used-cars/used-cars.service';
import {
  CarSearchMeta,
  SearchPageProps,
  Vehicle,
  VehicleType,
} from '@/types/used-cars/used-cars.types';

import { BlockWrapper } from '../block-wrapper/block-wrapper';
import { Breadcrumbs } from '../breadcrumbs/breadcrumbs';
import { SearchFilters } from '../search/search-filters/search-filters';
import { SearchResults } from '../search/search-results/search-results';
import { PageTitle } from '../typography/page-title/page-title';

const SearchPage: React.FC<SearchPageProps> = ({
  activeType,
  initialSearchQuery,
  brands,
  defaultCarPrice,
  bodyTypes,
  colourTypes,
  doorsTypes,
  defaultCarYears,
  defaultCarMileages,
  provincesWithDealerships,
  vehicles,
  vehiclesMeta,
  defaultConsumption,
  fuelTypeCount,
  transmissionCount,
  driveTrainCount,
  featuresCount,
  content,
  dealerships,
  activeDealership,
  newFilters,
}) => {
  const { setColorFilters } = useColorsFilter();
  const { setDoorsFilter } = useDoorsFilter();
  const { setBodyTypesFilter } = useBodyTypesFilter();
  const { setDefaultPriceFilter, setSearchPriceTypeFilter } = usePriceFilter();
  const { setYearsFilterDefault, setSearchYearTypeFilter } =
    useYearsFilterHook();
  const { setListOfDealers } = useDealersInfo();
  const { setBrandsRangesFilter, brandsRangesAllSelected } =
    useBrandsRangesFilter();
  const { setFeaturesFilter, setFeaturesCount } = useFeaturesFilter();
  const {
    provincesDealershipsFilter,
    setProvincesDealershipsFilter,
    setSearchLocationTypeFilter,
  } = useProvincesDealershipsFilter();
  const { setTransmissionTypeFilter, setTransmissionCount } =
    useTransmissionTypeFilter();
  const { setFuelTypeFilter, setFuelTypeCount } = useFuelTypeFilter();
  const { setDrivetrainTypesFilter, setDriveTrainCount } =
    useDrivetrainFilter();
  const {
    setVehicles,
    setVehiclesMeta,
    vehicles: vehiclesState,
    vehiclesMeta: vehiclesMetaData,
  } = useVehicles();
  const { setMileageFilterDefault, setSearchMileageTypeFilter } =
    useMileageFilter();
  const { setConsumptionFilterDefault } = useFuelConsumptionFilter();
  const {
    filtersQuery,
    activeCarType,
    setActiveCarType,
    setIsCleared,
    hasFiltersQuery,
    isCleared,
  } = useFiltersQuery();
  const { selectFiltersFromQueryParams } = useQueryParams();
  const { setIndexQuery } = useIndexSearch();

  const routes = useRoutesConfig();
  const searchParams = useSearchParams();
  const size = useWindowSize();

  const isFirstRender = useRef(true);

  const isMobile = size?.width && size?.width < minDesktop;

  useEffect(() => {
    setBodyTypesFilter(bodyTypes);
  }, [bodyTypes]);

  useEffect(() => {
    setActiveCarType(activeType as VehicleType);

    setBrandsRangesFilter(brands);

    if (brandsRangesAllSelected) {
      setBrandsRangesFilter((options) => {
        return produce(options, (draftOptions) => {
          const isAllChecked = draftOptions;

          draftOptions.forEach((option) => {
            option.isChecked = !isAllChecked;

            option.children?.forEach((child) => {
              child.isChecked = !isAllChecked;
            });
          });
        });
      });
    }
    setDefaultPriceFilter(defaultCarPrice);
    setColorFilters(colourTypes);
    setDoorsFilter(doorsTypes);
    setFeaturesFilter((features) => ({
      ...features,
      comfort: comfortOptions,
      safetyAndTech: safetyAndTechOptions,
      other: otherOptions,
    }));
    setFeaturesCount(featuresCount ?? []);
    setYearsFilterDefault(defaultCarYears);
    setMileageFilterDefault(defaultCarMileages);
    setConsumptionFilterDefault(defaultConsumption);
    setProvincesDealershipsFilter(
      provincesDealershipsFilter.length
        ? provincesDealershipsFilter
        : provincesWithDealerships,
    );
    setTransmissionTypeFilter(transmissionTypeOptions);
    setTransmissionCount(transmissionCount);
    setSearchYearTypeFilter(searchYearTypeOptions);
    setSearchLocationTypeFilter(provincesWithDealerships);
    setSearchPriceTypeFilter(searchPriceTypeOptions);
    setSearchMileageTypeFilter(searchMileageTypeOptions);
    setFuelTypeFilter(fuelTypeOptions);
    setFuelTypeCount(fuelTypeCount);
    setDrivetrainTypesFilter(driveTypeOptions);
    setDriveTrainCount(driveTrainCount);
    setVehiclesMeta(vehiclesMeta as CarSearchMeta);
    setIsCleared(false);
    setIndexQuery({ query: initialSearchQuery || '' });
    setListOfDealers(dealerships ?? []);
  }, []);

  useEffect(() => {
    setVehicles(vehicles as Vehicle[]);

    const sessionQuery = sessionStorage.getItem('filterQuery') || '{}';

    selectFiltersFromQueryParams(JSON.parse(sessionQuery));
    selectFiltersFromQueryParams(parseSearchParams(searchParams));

    isFirstRender.current = true;
  }, [vehicles, searchParams]);

  useEffect(() => {
    // New filters will do everything server-side so we don't do this call on the front-end
    if (newFilters) {
      return;
    }

    if (!isFirstRender.current) {
      if (filtersQuery && (hasFiltersQuery || isCleared)) {
        const sessionQuery = sessionStorage.getItem('filterQuery') || '{}';

        let meta = vehiclesMetaData;

        if (sessionQuery !== JSON.stringify(filtersQuery)) {
          meta = {
            ...vehiclesMetaData,
            currentPage: 1,
          };
        }

        if (isCleared) {
          sessionStorage.setItem('filterQuery', JSON.stringify({}));
        } else {
          sessionStorage.setItem('filterQuery', JSON.stringify(filtersQuery));
        }

        getVehiclesWithFilters(filtersQuery, meta, null, activeCarType).then(
          ({ vehicles: newVehicles, meta: newMeta }) => {
            if (!isEqual(newVehicles, vehiclesState)) {
              setVehicles(newVehicles);
            }
            if (meta.filterCount !== newMeta.filter_count) {
              setVehiclesMeta({
                ...meta,
                filterCount: newMeta.filter_count,
                isIndexSearch: false,
              });
            }
          },
        );
      } else {
        setIsCleared(false);
      }
    } else {
      isFirstRender.current = false;
    }
  }, [filtersQuery]);

  return (
    <React.Fragment>
      <BlockWrapper>
        <Breadcrumbs
          compact={isMobile || false}
          items={[
            {
              name:
                activeCarType == VehicleType.Demo
                  ? routes.demo.name
                  : routes.usedCars.name,
              path:
                activeCarType == VehicleType.Demo
                  ? routes.demo.path
                  : routes.usedCars.path,
            },
            {
              name: routes.searchResults.name,
              path: routes.searchResults.path,
            },
          ]}
        />
      </BlockWrapper>

      <BlockWrapper>
        <PageTitle key={activeCarType}>
          {activeDealership && activeDealership.name && activeDealership.city
            ? `Used Cars For Sale   ${activeDealership.city?.name}`
            : activeCarType == VehicleType.Demo
              ? 'Demo Used Cars For Sale'
              : 'Used Cars For Sale'}
        </PageTitle>
      </BlockWrapper>

      <SearchFilters
        pageType={activeCarType == VehicleType.Demo ? 'demo' : 'used'}
      />

      <SearchResults
        initialVehicles={vehicles}
        initialVehiclesMeta={vehiclesMeta}
        type={activeCarType}
        hideLocation={true}
      />

      {content && content.footer_call_to_action && (
        <DiscoverySection
          footer_call_to_action={content?.footer_call_to_action ?? null}
          footer_call_to_action_image={getImageLink(
            content.footer_call_to_action_image,
          )}
        />
      )}
    </React.Fragment>
  );
};

export default SearchPage;
